import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = window.__BACKEND_URL__;
const MAX_DESKTOP_WINDOWS = 3;

/**
 * MessagesContext — shared state for the floating messenger dock + header
 * dropdown. Supports up to 3 concurrent open chat windows on desktop; on
 * mobile only the topmost window is surfaced (single-thread sheet).
 *
 * Shape:
 *   windows: [{ id, minimised }]   — ordered oldest→newest
 *   totalUnread                    — derived from conversations[]
 *   openConversation(id)           — push (or raise to top) a window
 *   closeConversation(id)          — remove a window
 *   minimiseConversation(id, bool) — toggle minimised flag
 *   markConversationRead(id)       — zero unread locally + remotely
 *   refresh()                      — refetch conversations list
 */
const MessagesContext = createContext(null);

export function MessagesProvider({ children }) {
  const { user } = useAuth();
  const identityKey = user ? `${user.id}:${(user.active_role || user.role) === 'admin' ? 'admin' : 'member'}` : '';
  const currentIdentityKeyRef = useRef(identityKey);
  currentIdentityKeyRef.current = identityKey;
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [windows, setWindows] = useState([]); // [{id, minimised}]
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    if (!user) { setConversations([]); return; }
    const requestedIdentityKey = identityKey;
    setLoading(true);
    try {
      // /messages fetches BOTH active AND pending conversations (see
      // pages/MessagesPage.js). Do the same here so the header dropdown
      // badge counts unread messages sitting in message-request threads,
      // not just accepted conversations. Previously the badge silently
      // ignored pending threads and drifted from the /messages page.
      const [activeRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/api/conversations?status=active`, { withCredentials: true }),
        axios.get(`${API_URL}/api/conversations?status=pending`, { withCredentials: true }),
      ]);
      const listActive = Array.isArray(activeRes.data) ? activeRes.data : (activeRes.data?.conversations || []);
      const listPending = Array.isArray(pendingRes.data) ? pendingRes.data : (pendingRes.data?.conversations || []);
      // Dedupe by id in case a thread transitions between states mid-fetch.
      const byId = new Map();
      [...listActive, ...listPending].forEach((c) => {
        if (c && c.id) byId.set(c.id, c);
      });
      if (requestedIdentityKey === currentIdentityKeyRef.current) {
        setConversations(Array.from(byId.values()));
      }
    } catch {
      /* non-fatal */
    } finally {
      if (requestedIdentityKey === currentIdentityKeyRef.current) setLoading(false);
    }
  }, [user, identityKey]);

  // Clear popout windows and stale counts immediately on Admin/member switch.
  // No conversation or connection data is deleted by this UI change.
  useEffect(() => {
    setWindows([]);
    setConversations([]);
    if (!user) return undefined;
    fetchConversations();
    pollRef.current = setInterval(fetchConversations, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [identityKey, fetchConversations]);

  useEffect(() => {
    const onFocus = () => { if (user) fetchConversations(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, fetchConversations]);

  const totalUnread = useMemo(() => {
    if (!user) return 0;
    return conversations.reduce((sum, c) => {
      const u = (typeof c.unread_count_for_user === 'number')
        ? c.unread_count_for_user
        : (c.unread_count?.[user.id] || 0);
      return sum + (typeof u === 'number' ? u : 0);
    }, 0);
  }, [conversations, user]);

  const openConversation = useCallback((conversationId) => {
    if (!conversationId) return;
    setWindows((prev) => {
      const existing = prev.find((w) => w.id === conversationId);
      if (existing) {
        // Raise to top + un-minimise
        const others = prev.filter((w) => w.id !== conversationId);
        return [...others, { ...existing, minimised: false }];
      }
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
      if (isMobile) {
        // Mobile always shows a single window — replace current
        return [{ id: conversationId, minimised: false }];
      }
      // Desktop: keep up to MAX_DESKTOP_WINDOWS, drop oldest if over
      const next = [...prev, { id: conversationId, minimised: false }];
      return next.slice(-MAX_DESKTOP_WINDOWS);
    });
  }, []);

  const closeConversation = useCallback((conversationId) => {
    setWindows((prev) => prev.filter((w) => w.id !== conversationId));
  }, []);

  const minimiseConversation = useCallback((conversationId, next = true) => {
    setWindows((prev) => prev.map((w) => (w.id === conversationId ? { ...w, minimised: next } : w)));
  }, []);

  const closeAll = useCallback(() => setWindows([]), []);

  const markConversationRead = useCallback(async (conversationId) => {
    try {
      await axios.post(
        `${API_URL}/api/conversations/${conversationId}/mark-all-read`,
        {},
        { withCredentials: true }
      );
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? {
                ...c,
                unread_count_for_user: 0,
                unread_count: { ...(c.unread_count || {}), [user?.id]: 0 },
              }
            : c
        )
      );
    } catch { /* non-fatal */ }
  }, [user]);

  // Backwards-compatibility: a few callers read activeConversationId / widgetOpen.
  const activeConversationId = windows.length ? windows[windows.length - 1].id : null;
  const widgetOpen = windows.length > 0;

  const value = useMemo(() => ({
    conversations,
    loading,
    totalUnread,
    windows,
    activeConversationId,
    widgetOpen,
    openConversation,
    closeConversation,
    minimiseConversation,
    closeAll,
    refresh: fetchConversations,
    markConversationRead,
  }), [conversations, loading, totalUnread, windows, activeConversationId, widgetOpen, openConversation, closeConversation, minimiseConversation, closeAll, fetchConversations, markConversationRead]);

  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
}

export function useMessages() {
  const ctx = useContext(MessagesContext);
  if (!ctx) throw new Error('useMessages must be used within MessagesProvider');
  return ctx;
}
